import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Id, Doc } from '@convex/_generated/dataModel'
import { compressImage, type CompressionResult } from '@/lib/imageCompression'

// Re-export Convex types for convenience
export type ConvexBuildingImage = Doc<'building_images'>
export type BuildingImageId = Id<'building_images'>
export type BuildingId = Id<'buildings'>
export type StorageId = Id<'_storage'>

/**
 * Staged image for upload during building creation.
 * Tracks files that have been uploaded to storage but not yet linked to a building.
 */
export interface StagedBuildingImage {
  /** Local UUID for tracking in UI */
  id: string
  /** Convex storage ID */
  storageId: StorageId
  /** Local preview URL (blob URL) */
  previewUrl: string
  /** Original file reference */
  file: File
  /** Alt text for the image */
  altText?: string
  /** Image width in pixels */
  width?: number
  /** Image height in pixels */
  height?: number
  /** File size in bytes */
  fileSize?: number
  /** MIME type */
  mimeType?: string
  /** Compression status */
  compression?: {
    originalSize: number
    compressedSize: number
  }
}

/**
 * BuildingImage type used by components.
 * Adds computed fields and compatibility aliases.
 */
export interface BuildingImage {
  id: string // Maps to _id
  building_id: string
  storage_id: string
  url: string
  alt_text: string | null
  sort_order: number
  created_at: number
  // Computed field: first image (sort_order 0) is primary
  is_primary: boolean
}

/**
 * Convert a Convex building_images document to the component BuildingImage type.
 */
export function toBuildingImage(doc: ConvexBuildingImage, isPrimary?: boolean): BuildingImage {
  return {
    id: doc._id,
    building_id: doc.building_id,
    storage_id: doc.storage_id,
    url: doc.url,
    alt_text: doc.alt_text ?? null,
    sort_order: doc.sort_order,
    created_at: doc.created_at,
    is_primary: isPrimary ?? doc.sort_order === 0,
  }
}

/**
 * Hook for fetching all images for a building.
 * Uses Convex's real-time queries - data updates automatically when it changes.
 * Returns images converted to the component BuildingImage type with is_primary computed.
 */
export function useBuildingImages(buildingId: BuildingId | string | undefined) {
  const rawData = useQuery(
    api.buildingImages.listByBuilding,
    buildingId ? { building_id: buildingId as BuildingId } : 'skip'
  ) as ConvexBuildingImage[] | undefined

  // Convert Convex documents to component-friendly BuildingImage type
  const data = rawData?.map((doc: ConvexBuildingImage, index: number) => toBuildingImage(doc, index === 0))

  return {
    data,
    isLoading: rawData === undefined && buildingId !== undefined,
    error: null as Error | null, // Convex throws on error
  }
}

/**
 * Hook for generating an upload URL for Convex storage.
 * Returns a mutation function that returns a URL for uploading files.
 */
export function useGenerateBuildingImageUploadUrl() {
  const generateMutation = useMutation(api.buildingImages.generateUploadUrl)

  const mutate = async () => {
    return await generateMutation({})
  }

  return {
    mutate,
    mutateAsync: mutate,
  }
}

/**
 * Hook for saving an uploaded image to the building_images table.
 * Call this after uploading a file to the storage URL returned by useGenerateBuildingImageUploadUrl.
 */
export function useSaveBuildingImage() {
  const saveMutation = useMutation(api.buildingImages.saveBuildingImage)

  const mutate = async ({
    buildingId,
    storageId,
    altText,
  }: {
    buildingId: BuildingId | string
    storageId: Id<'_storage'>
    altText?: string
  }) => {
    return await saveMutation({
      building_id: buildingId as BuildingId,
      storage_id: storageId,
      alt_text: altText,
    })
  }

  return {
    mutate,
    mutateAsync: mutate,
  }
}

/**
 * Hook for uploading a building image to Convex storage with compression.
 * Combines generateUploadUrl, compression, and saveBuildingImage into a single operation.
 */
export function useUploadBuildingImage() {
  const generateUploadUrl = useMutation(api.buildingImages.generateUploadUrl)
  const saveImage = useMutation(api.buildingImages.saveBuildingImage)
  const [isPending, setIsPending] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [compressionProgress, setCompressionProgress] = useState<{
    originalSize: number
    compressedSize: number
  } | null>(null)

  const mutateAsync = useCallback(async ({
    buildingId,
    file,
    altText,
  }: {
    buildingId: BuildingId | string
    file: File
    altText?: string
  }) => {
    setIsPending(true)
    setError(null)
    setCompressionProgress(null)

    try {
      // Step 1: Compress the image
      setIsCompressing(true)
      let fileToUpload = file
      let width: number | undefined
      let height: number | undefined
      let fileSize: number | undefined
      let mimeType: string | undefined

      try {
        const result = await compressImage(file)
        fileToUpload = result.file
        width = result.width
        height = result.height
        fileSize = result.compressedSize
        mimeType = result.mimeType
        setCompressionProgress({
          originalSize: result.originalSize,
          compressedSize: result.compressedSize,
        })
      } catch (compressionError) {
        // If compression fails, use original file
        console.warn('Image compression failed, using original:', compressionError)
        fileSize = file.size
        mimeType = file.type
      }
      setIsCompressing(false)

      // Step 2: Get upload URL
      const uploadUrl = await generateUploadUrl({})

      // Step 3: Upload file to storage
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': fileToUpload.type },
        body: fileToUpload,
      })

      if (!response.ok) {
        throw new Error('Failed to upload file')
      }

      const { storageId } = await response.json()

      // Step 4: Save image record with metadata
      const imageId = await saveImage({
        building_id: buildingId as BuildingId,
        storage_id: storageId,
        alt_text: altText,
        width,
        height,
        file_size: fileSize,
        mime_type: mimeType,
      })

      return imageId
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      setIsCompressing(false)
    }
  }, [generateUploadUrl, saveImage])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    isCompressing,
    error,
    compressionProgress,
  }
}

/**
 * Hook for deleting a building image.
 * Removes both the storage file and the database record.
 */
export function useDeleteBuildingImage() {
  const deleteMutation = useMutation(api.buildingImages.remove)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const variablesRef = useRef<{ id: string; buildingId?: string } | null>(null)

  const mutateAsync = useCallback(async ({ id, buildingId }: { id: BuildingImageId | string; buildingId?: string }) => {
    setIsPending(true)
    setError(null)
    variablesRef.current = { id: id as string, buildingId }
    try {
      const result = await deleteMutation({
        id: id as BuildingImageId,
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      variablesRef.current = null
    }
  }, [deleteMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
    variables: variablesRef.current,
  }
}

/**
 * Hook for reordering images for a building.
 * Takes an array of image IDs in the new order.
 */
export function useReorderBuildingImages() {
  const reorderMutation = useMutation(api.buildingImages.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    buildingId,
    imageIds,
  }: {
    buildingId: BuildingId | string
    imageIds: (BuildingImageId | string)[]
  }) => {
    setIsPending(true)
    setError(null)
    try {
      const result = await reorderMutation({
        building_id: buildingId as BuildingId,
        image_ids: imageIds as BuildingImageId[],
      })
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}

// ============================================================================
// Staged Upload Hooks (for building creation flow)
// ============================================================================

/**
 * Hook for uploading a file directly to Convex storage.
 * Returns the storageId for later association with a building.
 * Used for staged uploads during building creation.
 */
export function useUploadBuildingImageToStorage() {
  const generateUploadUrl = useMutation(api.buildingImages.generateUploadUrl)
  const trackPendingUpload = useMutation(api.images.trackPendingUpload)
  const [isPending, setIsPending] = useState(false)
  const [isCompressing, setIsCompressing] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (file: File): Promise<StagedBuildingImage> => {
    setIsPending(true)
    setError(null)

    try {
      // Generate local ID for tracking
      const localId = crypto.randomUUID()

      // Step 1: Compress the image
      setIsCompressing(true)
      let compressionResult: CompressionResult | null = null
      let fileToUpload = file

      try {
        compressionResult = await compressImage(file)
        fileToUpload = compressionResult.file
      } catch (compressionError) {
        // If compression fails, use original file
        console.warn('Image compression failed, using original:', compressionError)
      }
      setIsCompressing(false)

      // Step 2: Get upload URL
      const uploadUrl = await generateUploadUrl({})

      // Step 3: Upload file to storage
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': fileToUpload.type },
        body: fileToUpload,
      })

      if (!response.ok) {
        throw new Error('Failed to upload file')
      }

      const { storageId } = await response.json() as { storageId: StorageId }

      // Step 4: Track in pending_uploads table
      await trackPendingUpload({ storage_id: storageId })

      // Create preview URL for display
      const previewUrl = URL.createObjectURL(fileToUpload)

      const stagedImage: StagedBuildingImage = {
        id: localId,
        storageId,
        previewUrl,
        file: fileToUpload,
        width: compressionResult?.width,
        height: compressionResult?.height,
        fileSize: compressionResult?.compressedSize ?? file.size,
        mimeType: compressionResult?.mimeType ?? file.type,
        compression: compressionResult
          ? {
              originalSize: compressionResult.originalSize,
              compressedSize: compressionResult.compressedSize,
            }
          : undefined,
      }

      return stagedImage
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
      setIsCompressing(false)
    }
  }, [generateUploadUrl, trackPendingUpload])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    isCompressing,
    error,
  }
}

/**
 * Hook for saving multiple staged images to a newly created building.
 * Batch operation to link all staged uploads to a building.
 */
export function useSaveBuildingImagesForNewBuilding() {
  const saveImagesMutation = useMutation(api.buildingImages.saveImagesForNewBuilding)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async ({
    buildingId,
    images,
  }: {
    buildingId: BuildingId | string
    images: StagedBuildingImage[]
  }) => {
    setIsPending(true)
    setError(null)

    try {
      const result = await saveImagesMutation({
        building_id: buildingId as BuildingId,
        images: images.map((img) => ({
          storage_id: img.storageId,
          alt_text: img.altText,
          width: img.width,
          height: img.height,
          file_size: img.fileSize,
          mime_type: img.mimeType,
        })),
      })

      // Clean up preview URLs after successful save
      images.forEach((img) => {
        if (img.previewUrl) {
          URL.revokeObjectURL(img.previewUrl)
        }
      })

      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      setError(error)
      throw error
    } finally {
      setIsPending(false)
    }
  }, [saveImagesMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}
